import { Fragment, useEffect, useState } from "react";
import { DateTimePicker, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { toast } from "react-toastify";
import moment from "moment";
import { CreateSeoSprintGbpPostsCSV, GetCampaignDefaultSettings, UpdateCampaign } from "../../../Services";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Link } from "react-router-dom";

const GbpPostingForm = ({ campaignData, onSubmit, validateSettingsRequiredFields, isLoading }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [settingsData, setSettingsData] = useState();
    const [gbpNumberOfPosts, setGbpNumberOfPosts] = useState("12");
    const [gbpScheduleDate, setGbpScheduleDate] = useState(
        moment().add(1, "day").set({
            hour: 9,
            minute: 0,
        })
    );
    const [gbpExcludeWeekends, setGbpExcludeWeekends] = useState(true);
    const [gbpPostsPerWeek, setGbpPostsPerWeek] = useState("3");
    const [seoSprintNextGBPPostDate, setSeoSprintNextGBPPostDate] = useState(null);

    useEffect(() => {
        getDefaultSettings();

        if (campaignData && campaignData.seoSprintNextGBPPostDate) {
            setSeoSprintNextGBPPostDate(moment(campaignData.seoSprintNextGBPPostDate));
        }
    }, []);

    const getDefaultSettings = async () => {
        let response = await GetCampaignDefaultSettings();

        if (response && response.data) {
            setSettingsData(response.data);
        }
    };

    const handleGenerateGbpPostsCsvFiles = async () => {
        if (typeof validateSettingsRequiredFields !== "undefined") {
            if (validateSettingsRequiredFields() === false) {
                toast.error("Please fill out required fields on Settings tab first.");
                return;
            }
        } else {
            if (
                !campaignData.seoSprintPrimaryServices ||
                campaignData.seoSprintPrimaryServices === "" ||
                !campaignData.seoSprintPrimaryServiceArea ||
                campaignData.seoSprintPrimaryServiceArea === "" ||
                !campaignData.seoSprintListOfServices ||
                campaignData.seoSprintListOfServices === "" ||
                !campaignData.seoSprintListOfLocations ||
                campaignData.seoSprintListOfLocations === ""
            ) {
                toast.error("Please fill out required SEO sprint settings fields first.");
                return;
            }
        }

        if (!gbpNumberOfPosts || gbpNumberOfPosts === "") {
            toast.error("Number of posts is required.");
            return;
        }

        if (!gbpPostsPerWeek || gbpPostsPerWeek === "") {
            toast.error("Posts per week is required.");
            return;
        }

        if (!gbpScheduleDate || gbpScheduleDate === "") {
            toast.error("Schedule date is required.");
            return;
        }

        setIsSubmitting(true);

        let data = {
            campaignId: campaignData.id,
            numberOfPosts: gbpNumberOfPosts,
            postsPerWeek: gbpPostsPerWeek,
            scheduleDate: moment.utc(gbpScheduleDate),
            excludeWeekends: gbpExcludeWeekends,
            aiModel: settingsData.seoSprintAIModel,
        };

        //create csv files
        const responseCSV = CreateSeoSprintGbpPostsCSV(campaignData.id, data);

        setTimeout(function () {
            if (onSubmit) {
                onSubmit();
            }
            setIsSubmitting(false);
        }, 1000);
    };

    const saveCampaignNextPostDate = async () => {
        if (campaignData) {
            setIsSaving(true);

            campaignData.seoSprintNextGBPPostDate = seoSprintNextGBPPostDate;

            const response = await UpdateCampaign(campaignData.id, campaignData);

            if (response.success) {
                toast.success("Next post date saved successfully.");
            }

            setIsSaving(false);
        }
    };

    return (
        <Fragment>
            <div className="row mb-2">
                <label htmlFor="gbpNumberOfPosts" className="form-label col-4">
                    Next Post Date:
                </label>
                <div className="col-8">
                    <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                        <DatePicker
                            name="seoSprintNextGBPPostDate"
                            className="form-control w-auto"
                            value={seoSprintNextGBPPostDate}
                            timezone="America/New_York"
                            onChange={(e) => setSeoSprintNextGBPPostDate(e)}
                            disabled={isSubmitting}
                        />
                    </LocalizationProvider>
                    <button className="btn btn-primary ms-2" type="button" onClick={saveCampaignNextPostDate} disabled={isLoading}>
                        {isSaving ? (
                            <Fragment>
                                <span className="spinner-border m-0 me-2" />
                                <span>{`Save`}</span>
                            </Fragment>
                        ) : (
                            <span>Save</span>
                        )}
                    </button>
                </div>
            </div>
            <div className="row mb-2">
                <div className="col-12">
                    <hr />
                </div>
            </div>
            <div className="row mb-3">
                <label htmlFor="gbpNumberOfPosts" className="form-label col-4">
                    Number Of Posts:
                </label>
                <div className="col-8">
                    <input
                        type="number"
                        className="form-control w-auto"
                        name="gbpNumberOfPosts"
                        value={gbpNumberOfPosts || ""}
                        onChange={(e) => setGbpNumberOfPosts(e.target.value)}
                        min={1}
                        max={50}
                        required
                        disabled={isSubmitting}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <label htmlFor="gbpPostsPerWeek" className="form-label col-4">
                    Posts Per Week:
                </label>
                <div className="col-8">
                    <input
                        type="number"
                        className="form-control w-auto"
                        name="gbpPostsPerWeek"
                        value={gbpPostsPerWeek || ""}
                        onChange={(e) => setGbpPostsPerWeek(e.target.value)}
                        min={1}
                        max={10}
                        required
                        disabled={isSubmitting}
                    />
                </div>
            </div>
            <div className="row mb-2">
                <label htmlFor="gbpScheduleDate" className="form-label col-4">
                    Schedule Date:
                </label>
                <div className="col-8">
                    <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                        <DateTimePicker
                            name="gbpScheduleDate"
                            className="form-control w-auto"
                            value={gbpScheduleDate || ""}
                            timezone="America/New_York"
                            onChange={(newValue) => setGbpScheduleDate(newValue)}
                            disabled={isSubmitting}
                        />
                    </LocalizationProvider>
                </div>
            </div>
            <div className="row mb-3">
                <label htmlFor="gbpExcludeWeekends" className="form-label col-4">
                    Exclude Weekends:
                </label>
                <div className="col-8">
                    <input
                        type="checkbox"
                        className="form-check-input"
                        name="gbpExcludeWeekends"
                        id="gbpExcludeWeekends"
                        checked={gbpExcludeWeekends}
                        onChange={(e) => setGbpExcludeWeekends(e.target.checked)}
                        disabled={isSubmitting}
                    />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col-12">
                    <button className="btn btn-primary" type="button" onClick={handleGenerateGbpPostsCsvFiles} disabled={isLoading}>
                        {isSubmitting ? (
                            <Fragment>
                                <span className="spinner-border m-0 me-2" />
                                <span>{`Generating CSV File...`}</span>
                            </Fragment>
                        ) : (
                            <span>Generate CSV File</span>
                        )}
                    </button>
                    {campaignData && typeof validateSettingsRequiredFields === "undefined" && (
                        <Link className="btn btn-link ms-2" to={`/setup/${campaignData.id}/seo-sprint`} target="_blank">
                            <span>Open SEO Sprint Settings</span>
                        </Link>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default GbpPostingForm;
